import { z } from 'zod'
import { COMMON_CLIENT_MESSAGES, type Flair, type PlayerPublic, type Role } from '../protocol'

/**
 * Everything two-seat turn games (Vanishing XOX, Connect Four) share on the wire. Seats are always
 * `X` (opens by default) and `O`; each game decides how to draw them.
 */
export type Seat = 'X' | 'O'

export function otherSeat(seat: Seat): Seat {
  return seat === 'X' ? 'O' : 'X'
}

export const GAME_IDS = ['xox', 'c4'] as const
export type GameId = (typeof GAME_IDS)[number]
export const gameIdSchema = z.enum(GAME_IDS)

/** `draw` only happens where a game's rules allow it (computer XOX, a full Connect Four board). */
export type DuelStatus = 'playing' | 'won' | 'forfeit' | 'draw'

// ---- turn clock (chosen by the creator, fixed for the room's lifetime) ----

export const TURN_SECONDS_MIN = 5
export const TURN_SECONDS_MAX = 120
export const TURN_SECONDS_DEFAULT = 20
/** How long each player has per move before forfeiting. */
export const turnSecondsSchema = z.number().int().min(TURN_SECONDS_MIN).max(TURN_SECONDS_MAX)

/** `pvp` = two humans; `cpu` = the human against the game's house bot, who always sits at X. */
export const ROOM_MODES = ['pvp', 'cpu'] as const
export type RoomMode = (typeof ROOM_MODES)[number]

/** Body of a room-creation POST: the game's settings patch plus who plays and who opens. */
export function duelRoomBodySchema<T extends z.ZodRawShape>(settingsPatch: z.ZodObject<T>) {
  return settingsPatch.extend({
    mode: z.enum(ROOM_MODES).optional(),
    /** cpu rooms only: the human moves first. Ignored for pvp. */
    humanFirst: z.boolean().optional(),
  })
}

export type EndReason = 'line' | 'timeout' | 'abandon' | 'draw'

/** Client → server frames every duel speaks; `move` carries the game's own intent field(s). */
export function duelClientMessageSchema<M extends z.ZodRawShape>(move: M) {
  return z.discriminatedUnion('type', [
    z.object({ type: z.literal('takeSeat') }),
    z.object({ type: z.literal('move'), expectedSeq: z.number().int().min(0), ...move }),
    z.object({ type: z.literal('rematch') }),
    z.object({ type: z.literal('leave') }),
    ...COMMON_CLIENT_MESSAGES,
  ])
}

export interface DuelSnapshot<G, S> {
  code: string
  seq: number
  /** The creator; when they leave, the room closes for everyone. */
  hostId: string
  seats: { X: PlayerPublic | null; O: PlayerPublic | null }
  mode: RoomMode
  game: G
  series: { X: number; O: number }
  rematch: { X: boolean; O: boolean }
  /** Server epoch ms when the current turn expires; null when no clock is running. */
  deadlineAt: number | null
  settings: S
  presence: { X: boolean; O: boolean }
  spectators: number
  /** Who moves first in the next game. */
  nextFirst: Seat
}

export function roleOf(
  snapshot: { seats: { X: { id: string } | null; O: { id: string } | null } },
  playerId: string,
): Role {
  if (snapshot.seats.X?.id === playerId) return 'X'
  if (snapshot.seats.O?.id === playerId) return 'O'
  return 'spectator'
}

/** Error codes every duel's `handle()` can report on top of the transport's `ErrorCode`. */
export const DUEL_ERROR_CODES = [
  'NOT_YOUR_TURN',
  'NOT_SEATED',
  'SEAT_TAKEN',
  'GAME_OVER',
  'TIMED_OUT',
] as const
export const DUEL_ERROR_TEXT: Record<(typeof DUEL_ERROR_CODES)[number], string> = {
  NOT_YOUR_TURN: "It's not your turn",
  NOT_SEATED: 'You are watching this game',
  SEAT_TAKEN: 'Both seats are taken',
  GAME_OVER: 'The game is over',
  TIMED_OUT: 'Time ran out',
}

// ---- HTTP payloads shared by every duel ----

export interface RoomSummary {
  code: string
  seats: { X: PlayerPublic | null; O: PlayerPublic | null }
  spectators: number
  status: DuelStatus
}

export interface LeaderboardEntry extends Flair {
  playerId: string
  nickname: string
  wins: number
  losses: number
  games: number
  streak: number
}

export interface HistoryEntry {
  id: string
  game: GameId
  roomCode: string
  opponent: { id: string; nickname: string } | null
  youWere: Seat
  result: 'win' | 'loss' | 'draw'
  endReason: EndReason
  mode: RoomMode
  moveCount: number
  endedAt: string
}

/**
 * One human's record against a house bot. `losses` are lines and timeouts the bot won; `wins`
 * are games the human won; `quits` are abandons.
 */
export interface CpuStatsPlayer extends Flair {
  playerId: string
  nickname: string
  games: number
  wins: number
  losses: number
  draws: number
  quits: number
  /** Most moves survived in a single game. */
  longestGame: number
  lastPlayedAt: string
}

export interface CpuStats {
  game: GameId
  totals: { games: number; wins: number; losses: number; draws: number; quits: number }
  players: CpuStatsPlayer[]
  /** Server configuration the boss card explains to players. */
  rules: { edgeOpening: boolean }
}
